import { useMemo, useEffect, useState } from 'react';
import _ from 'lodash';
import { useDebug } from '@tradingblock/components';
import { useStateSelector, loadedoptionChains } from '../../../data/global/dataSelectors';
import { ExpirationOptionPair } from '../../../types';
import { AssetSymbol } from '@tradingblock/types';
import { useOptionChainData } from '../state/OptionChainState';
import { useQuoteActions } from '../../../hooks/useQuoteData';
import { areArraysEqual } from '../../../utilities/data';
import { useBlockGlobalData, useBlockId } from '../../../components/blocks/BlockState';
import { useDispatcher } from '../../../data/global/hooks';
import { expirationValue } from '../../../utilities/date';

export const useOptionChainSymbols = (assetSymbol: AssetSymbol) => {
  const { dispatcher } = useDispatcher();
  const { symbol } = assetSymbol;
  const log = useDebug('useOptionChainSymbols');
  const blockId = useBlockId();
  const [subscribedSymbols, setSubscribedSymbols] = useState<string[]>([]);
  const { setQuotes, addQuote } = useQuoteActions();
  const { quotes } = useBlockGlobalData();
  const expandedExpirations = useOptionChainData((s: any) => s.expandedExpirations);

  const optionchain = useStateSelector(s => loadedoptionChains(s, symbol || '', blockId));
  const filteredChainData = _.filter(optionchain, (d: ExpirationOptionPair) => _.includes(_.map(expandedExpirations, expirationValue), expirationValue(d.expiration)));
  const symbolsForData = useMemo(() => {
    if (!symbol) {
      return [];
    }

    // Subscribe to visible strikes + buffer below for better performance
    const maxStrikes = 10; // Visible strikes

    return _.reduce(
      filteredChainData,
      (symbols: string[], expiration) => {
        // Apply subscription filtering - subscribe to visible strikes + buffer
        let visiblePairs = expiration.pairs;

        if (expiration.pairs.length > maxStrikes) {
          // Find the current strike or use the median as starting point
          const currentStrike = expandedExpirations.find((e: any) =>
            expirationValue(e) === expirationValue(expiration.expiration)
          );
          const currentStrikeValue = currentStrike ? currentStrike.currentStrike : undefined;

          if (currentStrikeValue) {
            const startingStrikeIndex = expiration.pairs.findIndex(
              (pair: any) => pair.Strike === currentStrikeValue
            );
            if (startingStrikeIndex !== -1) {
              // Subscribe to visible rows + buffer below instead of centering around current price
              // This ensures we have quotes for the visible area plus some buffer for scrolling
              const visibleStrikes = maxStrikes; // 10 visible strikes
              const bufferBelow = 5; // 5 additional strikes below for scrolling
              const totalStrikes = visibleStrikes + bufferBelow; // 15 total strikes

              // Start from the current strike and go down (including buffer)
              const startIndex = Math.max(0, startingStrikeIndex);
              const endIndex = Math.min(expiration.pairs.length, startIndex + totalStrikes);
              visiblePairs = expiration.pairs.slice(startIndex, endIndex);
            } else {
              // If current strike not found, take middle 10 strikes
              const middleIndex = Math.floor(expiration.pairs.length / 2);
              const halfStrikes = Math.floor(maxStrikes / 2);
              const startIndex = Math.max(0, middleIndex - halfStrikes);
              const endIndex = Math.min(expiration.pairs.length, startIndex + maxStrikes);
              const adjustedStartIndex = Math.max(0, endIndex - maxStrikes);
              visiblePairs = expiration.pairs.slice(adjustedStartIndex, endIndex);
            }
          } else {
            // If no current strike, take middle 10 strikes
            const middleIndex = Math.floor(expiration.pairs.length / 2);
            const halfStrikes = Math.floor(maxStrikes / 2);
            const startIndex = Math.max(0, middleIndex - halfStrikes);
            const endIndex = Math.min(expiration.pairs.length, startIndex + maxStrikes);
            const adjustedStartIndex = Math.max(0, endIndex - maxStrikes);
            visiblePairs = expiration.pairs.slice(adjustedStartIndex, endIndex);
          }
        }

        const expPairs = _.reduce(
          visiblePairs,
          (pairSymbols: string[], pair) => {
            return [...pairSymbols, pair.Call.Symbol, pair.Put.Symbol];
          },
          []
        );
        return _.union(symbols, expPairs);
      },
      []
    );
  }, [filteredChainData, symbol, expandedExpirations, blockId]);
  useEffect(() => {
    if (symbol) {
      addQuote(symbol);
    }
  }, [symbol, addQuote]);

  useEffect(() => {
    if (!areArraysEqual(subscribedSymbols, symbolsForData)) {
      log('block symbols %o', {
        blockId,
        fetchAndSubscribe: [symbol],
        subscribe: symbolsForData,
        previousSubscribed: subscribedSymbols
      });
      setSubscribedSymbols(symbolsForData);
      setQuotes({ subscribe: [...symbolsForData, symbol] });
    } else if (!quotes.includes(symbol)) {
      setQuotes(symbol);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsForData, symbol]);

  useEffect(() => {
    dispatcher.expirations.request(symbol);
  }, [symbol]);

  return symbolsForData;
};
